import React from 'react';
import Head from 'next/head';
import { getCookie } from 'cookies-next';
import { Button } from '@components/atoms/Button/Button';
import { Collection } from '@components/atoms/Collection/Collection';
import { Meta } from '@components/atoms/MetaTags/Meta';
import { Oewa } from '@components/atoms/Oewa/Oewa';
import { Title } from '@components/atoms/Title';
import { type Collection as CollectionType, GetTvmCollectionsPaginatedDocument } from '@src/lib/graphql/generated';
import { createUrqlClient, usePaginatedQuery } from '@lib/graphql/urql';
import { useTagManager } from '@lib/hooks/useTagManager';

export function CollectionsIndex({ data }) {
  const [{ data: collectionsData, hasMorePages, fetchNextPage, fetching }] = usePaginatedQuery({
    query: GetTvmCollectionsPaginatedDocument,
    variables: {
      page: 1,
      first: 20,
      itemAmount: 3,
      itemsOnlyWithImages: true,
    },
    requestPolicy: 'cache-first',
  });

  const collections: CollectionType[] = collectionsData.flatMap((page) => page.collectionsPaginated?.data);

  useTagManager({
    event: 'metadata',
    loginStatus: 'nicht eingeloggt',
    seitentyp: 'Collections',
    titel: 'Collections',
    aktualisierungsdatum: data?.updated_at,
    erstellungsdatum: data?.created_at,
    oewaSeitenkontingent: 'Service',
    oewaSubkontingent: 'Programmeundlokalfuehrer',
    oewaProfilingkontingent: 'TVProgramme',
    cookieAlert: getCookie('OptanonConsent'),
  });

  return (
    <div>
      <Oewa pageKey={'Service'} categoryKey={'Programmeundlokalfuehrer'} profileKey={'TVProgramme'} />
      <Head>
        <title>TV-Media</title>
        {Meta(
          {
            ...data?.metadata,
            open_graph_image:
              data?.random_item?.cover_image?.url ??
              data?.random_item?.backdrop_image?.url ??
              data?.metadata?.open_graph_image,
          },
          data?.title,
        )}
      </Head>
      <Title classProps={{ heading: 'text-xl lg:text-xl mb-6' }} level={1}>
        Collections
      </Title>
      <div className="grid grid-cols-fill-streaming-collections gap-4">
        {collections?.length > 0 &&
          collections?.map((collection) => <Collection key={collection.id} collection={collection} />)}
      </div>
      {hasMorePages && (
        <div className="my-8 text-center">
          <Button buttonStyle="primary" onAction={fetchNextPage} hasAction isLoading={fetching} title="Mehr laden" />
        </div>
      )}
    </div>
  );
}

export const collectionsSsrLogic = async () => {
  const { ssrQuery, ssrCache } = createUrqlClient();
  const { error } = await ssrQuery({
    query: GetTvmCollectionsPaginatedDocument,
    variables: {
      page: 1,
      first: 20,
      itemAmount: 3,
      itemsOnlyWithImages: true,
    },
  });

  console.log({ error });

  if (error) {
    console.error(error);
    return { notFound: true };
  }

  return {
    revalidate: 60 * 60 * 24 * 2, // 2 days
    props: {
      slug: 'index',
      urqlState: error ? null : ssrCache.extractData(),
    },
  };
};
